// Presence socket (Layer B1). While it is open the signed-in user counts as
// online; it also streams other users' status transitions, and in Layer B2 it
// will carry incoming-call rings.
//
// Runs OUTSIDE the React tree (§9.3.1): it writes straight into presenceStore.
// Metadata only — no keys, no content ever cross this socket.
//
// Liveness mirrors the admission WS: the server sends a JSON "hb" every 20s, so
// a socket that goes quiet for STALE_MS is presumed half-dead (sleep/roaming/
// proxies) and force-closed, which triggers the normal reconnect path.

import type { CallInfo } from './api';
import { onChatPing } from './chat/chatClient';
import { desktopNotifyCall } from './desktop';
import {
  bumpMissed,
  clearCall,
  setActiveCall,
  setCallEnded,
  setCallOutcome,
  setIncomingCall,
} from './store/callsStore';
import {
  PresenceStatus,
  resetPresence,
  setPresence,
  setPresenceConnected,
  setPresenceSnapshot,
  setRingtone,
  setSelfDnd,
} from './store/presenceStore';

interface PresenceEvent {
  t:
    | 'hello'
    | 'presence'
    | 'hb'
    | 'call.incoming'
    | 'call.accepted'
    | 'call.declined'
    | 'call.canceled'
    | 'call.missed'
    | 'call.busy'
    | 'call.invited'
    | 'call.left'
    | 'call.ended'
    | 'chat';
  userId?: string;
  status?: PresenceStatus;
  users?: Record<string, PresenceStatus>;
  call?: CallInfo;
  ringtone?: string;
  dnd?: boolean;
  /** "chat" wake-up ping (Layer CH): fetch envelopes, never content. */
  chat?: { conversationId: string; seq: number };
}

/** MY own leg of a call, or null if I am not one of the people it rang. A
 * group call broadcasts every leg's outcome to everyone, so "did this event
 * happen to ME" is the question almost every branch below has to ask. */
function myLeg(call: CallInfo, userID: string): string | null {
  const p = (call.peers ?? []).find((x) => x.id === userID);
  if (p) return p.state;
  return call.calleeId === userID ? call.state : null;
}

/** Apply a call.* event. The ring surfaces own the audio; this moves state and,
 * on desktop, raises the native notification so a call still reaches you with
 * the window closed (the Layer A bridge).
 *
 * The group rule (B5): a leg's outcome is NOT the call's. Someone else
 * declining, missing or leaving must never silence your ring or tear down a
 * call you are still on — the server sends call.canceled/call.ended when the
 * call itself is over, and only that ends it for everybody. */
function handleCallEvent(ev: PresenceEvent): void {
  const call = ev.call;
  if (!call) return;
  const group = call.kind === 'group';
  const mine = selfID ? myLeg(call, selfID) : null;
  switch (ev.t) {
    case 'call.incoming':
      setIncomingCall(call);
      desktopNotifyCall({
        title: group ? 'Incoming group call' : 'Incoming call',
        body: `${call.callerName || 'Someone'} is calling you`,
        callId: call.id,
      });
      break;
    case 'call.accepted':
      // The caller learns someone picked up. In a group, a peer who is still
      // being rung must NOT have their sheet cleared because someone else
      // answered first — only the answerer and the caller move on.
      if (!group || selfID === call.callerId || mine === 'active') setActiveCall(call);
      break;
    case 'call.declined':
    case 'call.missed': {
      const missed = ev.t === 'call.missed';
      if (group) {
        // Not my leg → someone else's outcome; nothing changes for me.
        if (mine !== 'declined' && mine !== 'missed') break;
        if (missed) bumpMissed();
        // Scoped to THIS call: another call could be ringing right now, and
        // clearing the sheet blindly would silence the wrong one.
        clearCall(call.id); // my ring is over; the call itself may well continue
        break;
      }
      if (missed && selfID && call.calleeId === selfID) bumpMissed();
      setCallOutcome(missed ? 'No answer' : 'Call declined');
      setCallEnded(call.id, missed ? 'No answer' : 'Call declined');
      clearCall(call.id);
      break;
    }
    case 'call.busy':
      // Someone tried to reach us while we were busy or on DND. It must NOT
      // ring or open a sheet — that is the whole point — but hiding the attempt
      // entirely would be worse than the interruption we are preventing.
      bumpMissed();
      break;
    case 'call.invited':
    case 'call.left':
      // Someone was added, or hung up, and the call goes on either way. The
      // room's own participant events repaint the stage — a ring you are NOT
      // part of must not touch your call state.
      break;
    case 'call.canceled':
      setCallEnded(call.id, 'Call canceled');
      clearCall(call.id);
      break;
    case 'call.ended':
      setCallEnded(call.id, 'Call ended');
      clearCall(call.id);
      break;
  }
}

const STALE_MS = 60_000;
const STALE_CHECK_MS = 15_000;
const MAX_BACKOFF_MS = 30_000;

function wsURL(): string {
  const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${window.location.host}/v1/presence`;
}

// Our own user id — call.missed reaches BOTH parties, but only the CALLEE
// actually missed a call (for the caller it was a no-answer).
let selfID = '';

let ws: WebSocket | null = null;
let stopped = true;
let attempts = 0;
let retryTimer: number | null = null;
let staleTimer: number | null = null;

function clearTimers(): void {
  if (retryTimer !== null) window.clearTimeout(retryTimer);
  if (staleTimer !== null) window.clearInterval(staleTimer);
  retryTimer = null;
  staleTimer = null;
}

function connect(): void {
  if (stopped) return;
  let lastSeen = Date.now();
  const sock = new WebSocket(wsURL());
  ws = sock;

  sock.onopen = () => {
    attempts = 0;
    lastSeen = Date.now();
    setPresenceConnected(true);
  };

  sock.onmessage = (e) => {
    lastSeen = Date.now();
    let ev: PresenceEvent;
    try {
      ev = JSON.parse(e.data as string) as PresenceEvent;
    } catch {
      return;
    }
    if (ev.t === 'hello') {
      setPresenceSnapshot(ev.users ?? {});
      if (ev.ringtone) setRingtone(ev.ringtone);
      setSelfDnd(ev.dnd === true);
      // A fresh socket means a window where chat pings could not reach us —
      // catch up now rather than waiting for the next message.
      onChatPing();
    }
    else if (ev.t === 'presence' && ev.userId && ev.status) setPresence(ev.userId, ev.status);
    else if (ev.t.startsWith('call.')) handleCallEvent(ev);
    else if (ev.t === 'chat') onChatPing();
    // "hb" only refreshes liveness.
  };

  sock.onclose = () => {
    setPresenceConnected(false);
    if (staleTimer !== null) window.clearInterval(staleTimer);
    staleTimer = null;
    if (stopped) return;
    // Exponential backoff, capped — a signed-out user 401s and simply keeps
    // retrying slowly rather than hammering the server.
    attempts += 1;
    const delay = Math.min(1000 * 2 ** (attempts - 1), MAX_BACKOFF_MS);
    retryTimer = window.setTimeout(connect, delay);
  };

  sock.onerror = () => sock.close();

  staleTimer = window.setInterval(() => {
    if (Date.now() - lastSeen > STALE_MS) sock.close();
  }, STALE_CHECK_MS);
}

/** Open the presence socket (idempotent). Call once the user is signed in. */
export function startPresence(userID = ''): void {
  if (!stopped) return;
  selfID = userID;
  stopped = false;
  attempts = 0;
  connect();
}

/** Close the presence socket and clear status (sign-out / teardown). */
export function stopPresence(): void {
  stopped = true;
  clearTimers();
  ws?.close();
  ws = null;
  resetPresence();
}
